import * as React from 'react';
import { useBrowser } from '../language/useBrowser';
import { useProjIds } from '../navigation/hooks';
import { useRoutes } from '../navigation/routes';
import truScoreLogo from '../assets/logo_tbc.svg';
import hrdqLogo from '../assets/logo_hrdq.gif';
import zfLogo from '../assets/logo_zf.svg';
import talentSmartLogo from '../assets/logo_talentsmart.png';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN } from '../layout/flexBox';
import { css } from '@emotion/core';
import { Link } from 'react-router-dom';
import BulletedMsg from '../layout/BulletedMsg';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { Str } from '../localization/translationKeys';
import AppActions from '../project/sideMenu/AppActions';
import Msg from '../localization/Msg';
import { COLORS } from '../globalStyle/colors';
import { useAppState } from '../core/redux/hooks';
import { useMemo } from 'react';
import { useCurrentUser } from '../auth/hooks';


const styles = {
  root:           css`
    padding: 0 10px 15px;
    box-sizing: border-box;

    overflow-y: scroll;
    -ms-overflow-style: none; // IE-specific
    scrollbar-width: none; // Firefox-specific

    ::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }
  `,
  logo:           css`
    width: 184px;
    margin: 20px auto; 
    padding: 0;
  `,
  appActions:     css`
    margin-top: 15px;
    margin-bottom: 15px;
  `,
  footer:         css`
    font-size: 11.5px;
    color: ${COLORS.MEDIUM_GREY};
    
    & > a { 
      text-decoration: none;
      color: ${COLORS.BLUE}
    }
    
    & > a:hover { 
      text-decoration: underline;
    }
    
    @supports (-webkit-touch-callout: none) {
      margin-bottom: 60px;
    }
  `,
  linkContainer:  css`
    border: 1px solid ${COLORS.LIGHTER_GREY};
    border-radius: 8px;
    
    & > * {
      border-bottom: 1px solid ${COLORS.LIGHTER_GREY};
    }
    
    & > *:last-child {
      border-bottom: none;
    }
  `,
  link:           css`
    display: block;
    padding: 18px 15px;
    text-decoration: none;
    
    &:hover {
      cursor: pointer;
    }
  `,
  inactiveLink:   css`
    color: ${COLORS.MEDIUM_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  activeLink:     css`
    color: ${COLORS.DARK_GREY};
    background-color: ${COLORS.LIGHTER_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  iconMediumGrey: css`
    font-size: 15px;
    color: ${COLORS.MEDIUM_GREY};
  `,
  iconGreen:      css`
    font-size: 15px;
    color: ${COLORS.GREEN};
  `,
};


type Props = {
  className?: string;
};


const activeLink = (route: string) => window.location.pathname.endsWith(route);

// TODO - refactor common code out of `RaterApprovalMenu`, hoist props to implement for each flow
/**
 * RaterApprovalMenu: a component that renders the contents of the side menu on report recipient pages.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientMenu: React.FC<Props> = props => {
  const { onIe } = useBrowser();

  const projIds = useProjIds();
  const routes  = useRoutes(projIds.participantId, projIds.projDisplayId);

  const config = useAppState(state => state.config);
  const system = (config?.system || '').toLowerCase();
  const projectLogo =  React.useMemo (
    () => system === 'zf'
            ? zfLogo
            : system === 'hrdq'
              ? hrdqLogo
              : system === 'talentsmart'
                ? talentSmartLogo
                : truScoreLogo // always default to TBC
    ,
    [system],
  ); 

  const currentUser  = useCurrentUser();
  const hasDashboardPermission = useMemo(
    () => currentUser?.hasReportRecipientAggregateDashboardPermission || false,
     [currentUser],
  );

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]} className={props.className}>
      {
        <LocalDiv css={css`height: 48px;`} />
      }

      {
        onIe
          ? (
            <div css={[styles.logo]}>
              <img src={projectLogo} alt='Project Logo' />
            </div>
          )
          : (
            <img src={projectLogo} alt='Project Logo' css={[styles.logo]} />
          )
      }

      {/* Links */}
      <div css={styles.linkContainer}>
        <Link
          to={routes.ReportRecipientDashboard}
          css={[
            styles.link,
            activeLink(routes.ReportRecipientDashboard) ? styles.activeLink : styles.inactiveLink,
          ]}
        >
          <BulletedMsg
            icon={faCircle}
            msg={Str.ReportPortal}
            iconStyle={styles.iconGreen}
            spacePx={14}
          />
        </Link>
        { hasDashboardPermission && (
            <Link
              to={routes.ReportRecipientAggregateDashboard}
              css={[
                styles.link,
                activeLink(routes.ReportRecipientAggregateDashboard) ? styles.activeLink : styles.inactiveLink,
              ]}
            >
              <BulletedMsg
                icon={faCircle}
                msg={Str.AggregateDashboard}
                iconStyle={styles.iconGreen}
                spacePx={14}
              />
            </Link>
          )
        }
      </div>

      {/* Options */}
      <AppActions
        css={styles.appActions}
        loginUrl={routes.ReportRecipientLogin}
        accountUrl={routes.ReportRecipientAccountConfig}
        languageUrl={routes.ReportRecipientLanguage}
        customerServiceUrl={routes.ReportRecipientCustomerService}
      />

      {/* Footer: Copyright/Privacy Policy  */}
      <Msg defaultMsg='' str={Str.FooterCopyrightPrivacy} css={styles.footer} />
    </LocalDiv>
  );
};

export default ReportRecipientMenu;
